import React, { useState } from 'react';

import { Icon, Pressable } from '@rbilabs/universal-components';
import { CardTypes, IRevaultInformation } from 'features/payment/types';
import { shouldReVault } from 'features/payment/utils';

import { PaymentMethodAddNewItemOption } from 'components/payment-method-add-new-item-option';
import PaymentMethodOption from 'components/payment-method-option';
import { MastercardPartnershipAlert } from 'pages/loyalty/loyalty-partners/mastercard/mastercard-partnership-alert';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { primitive } from 'styles/constants/primitives';
import noop from 'utils/noop';

import { ADD_PREPAID_CARD } from './constants';
import { DisplayToggle } from './display-toggle';
import { PaymentMethodOptions } from './payment-method-options';
import {
  PaymentMethodSelect,
  StyledAddNewCreditCardIcon,
  StyledAddPaymentMethodIcon,
  ToggleWrapper,
} from './styled';
import { CurrentSelectedMethodType, IPaymentMethodProps } from './types';
import { classifyPaymentMethods, getCurrentSelectedMethodType } from './utils';

function PaymentMethod({
  methods,
  selectedMethodId,
  handleSelect,
  isAddCardOpen = false,
  openAddCardOnPress,
  isAddGiftCardMethodSelected = false,
  setIsAddGiftCardMethodSelected = noop,
  isOrbitalPaymentEnabled = false,
  isQuickPay = false,
  setIsQuickPayMethodSelected = noop,
}: IPaymentMethodProps) {
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);

  const [collapsed, setCollapsed] = useState(true);

  const { selectedMethod, unSelectedMethods, prepaidPaymentMethod } = classifyPaymentMethods(
    methods,
    selectedMethodId
  );

  const showAddNewGiftCardOption =
    !isQuickPay && !isAddGiftCardMethodSelected && enableGiftCard && !prepaidPaymentMethod;
  const showAddNewCardOption = !isAddCardOpen;

  const isCurrentSelectedMethodOptionClickable =
    showAddNewGiftCardOption || showAddNewCardOption || !!unSelectedMethods.length;

  function toggleCollapsed() {
    setCollapsed(currentCollapsed => !currentCollapsed);
  }
  function getSelectedMethodClickHandler() {
    return isCurrentSelectedMethodOptionClickable ? toggleCollapsed : undefined;
  }

  let SelectedPaymentMethodOptionItem = null;

  if (selectedMethod) {
    const accountIdentifier =
      selectedMethod?.accountIdentifier ?? selectedMethod?.fdAccountId ?? '';
    const revaultInformation: IRevaultInformation = shouldReVault({
      accountIdentifier,
      isOrbitalPaymentEnabled,
      methods,
    });

    SelectedPaymentMethodOptionItem = (
      <PaymentMethodOption
        method={selectedMethod}
        onPress={getSelectedMethodClickHandler()}
        disableHover={collapsed}
        disableMethod={!!selectedMethod.prepaid && !enableGiftCard}
        hideBorder
        fromCheckout
        revaultInformation={revaultInformation}
        collapsed={collapsed}
        isRoundedBorder
        isQuickPay={isQuickPay}
        setIsQuickPayMethodSelected={setIsQuickPayMethodSelected}
        selected
      />
    );
  }

  const SelectedAddNewPaymentMethodOptionItem = (
    <PaymentMethodAddNewItemOption
      testID="add-new-payment-method"
      text={t('addNewPaymentMethod')}
      Icon={<StyledAddPaymentMethodIcon />}
      onPress={getSelectedMethodClickHandler()}
      selected
    />
  );
  const SelectedAddNewCardOptionItem = (
    <PaymentMethodAddNewItemOption
      testID="add-new-card"
      text={t('addNewCard')}
      Icon={<StyledAddNewCreditCardIcon />}
      onPress={getSelectedMethodClickHandler()}
      selected
    />
  );
  const SelectedAddNewGiftCardOptionItem = (
    <PaymentMethodAddNewItemOption
      testID="add-gift-card"
      text={t(ADD_PREPAID_CARD)}
      Icon={<Icon variant="crownCreditCard" />}
      onPress={getSelectedMethodClickHandler()}
      selected
    />
  );
  const currentSelectedMethodType = getCurrentSelectedMethodType({
    isAddGiftCardMethodSelected,
    isAddNewCardMethodSelected: isAddCardOpen,
    selectedMethodExist: !!selectedMethod,
  });
  const CurrentSelectedMethodMap = {
    [CurrentSelectedMethodType.SELECTED_PAYMENT_METHOD_OPTION]: SelectedPaymentMethodOptionItem,
    [CurrentSelectedMethodType.ADD_NEW_PAYMENT_METHOD]: SelectedAddNewPaymentMethodOptionItem,
    [CurrentSelectedMethodType.ADD_NEW_CARD]: SelectedAddNewCardOptionItem,
    [CurrentSelectedMethodType.ADD_NEW_GIFT_CARD]: SelectedAddNewGiftCardOptionItem,
  };
  const CurrentSelectedMethod = CurrentSelectedMethodMap[currentSelectedMethodType];

  const showMastercardPartnership = useFlag(LaunchDarklyFlag.SHOW_MASTERCARD_PARTNERSHIP);

  return (
    <>
      <PaymentMethodSelect testID="payment-methods">
        <Pressable onPress={toggleCollapsed}>
          {CurrentSelectedMethod}
          {isCurrentSelectedMethodOptionClickable && (
            <ToggleWrapper>
              <DisplayToggle collapsed={collapsed} onPress={toggleCollapsed} />
            </ToggleWrapper>
          )}
        </Pressable>

        <PaymentMethodOptions
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          unSelectedMethods={unSelectedMethods}
          handleSelect={handleSelect}
          setIsAddNewCardOptionSelected={openAddCardOnPress}
          setIsAddNewGiftCardOptionSelected={setIsAddGiftCardMethodSelected}
          showAddNewGiftCardOption={showAddNewGiftCardOption}
          showAddNewCardOption={showAddNewCardOption}
          isQuickPay={isQuickPay}
          setIsQuickPayMethodSelected={setIsQuickPayMethodSelected}
          showMastercardPartnership={
            showMastercardPartnership &&
            !methods.some(method => method.credit?.cardType === CardTypes.MASTERCARD)
          }
        />
      </PaymentMethodSelect>
      {showMastercardPartnership && collapsed && !isAddCardOpen && (
        <MastercardPartnershipAlert style={{ marginTop: primitive.spacing.$2 }} />
      )}
    </>
  );
}

export default PaymentMethod;
